/**
 * The wire bodies, as export rows.
 *
 * **The export reflects what is displayed** — current filters, drafts and hand edits included —
 * so every function here takes the ALREADY-FILTERED arrays the panels are drawing and nothing
 * re-fetches or re-derives a scope of its own. The status words and the violation verdicts come
 * from the same functions the two fixture lists print, so a sheet cannot disagree with the row
 * it was taken from.
 *
 * Pure, and separate from `workbook.ts`, because these are the only functions that know the API's
 * shapes: a change to a wire body lands here, and the workbook builders go on taking plain rows.
 */

import { tidyCriterionDisplayName } from "@hockeytd/shared";

import type {
  AppointmentFixtureBody,
  AppointmentSlotBody,
  ConsolidatedEvidenceNote,
  UmConsolidatedEvidenceBody,
  UmpireFixtureBody,
  UmpireSlotBody,
} from "../../api/types.js";
import type { MatrixRow } from "../appointments/derive.js";
import { fixtureStatus } from "../appointments/FixtureListPanel.js";
import { VISIBILITY_LABEL } from "../um/UmEvidencePane.js";
import type { UmpireMatrixRow as DerivedUmpireMatrixRow } from "../umpires/derive.js";
import type { UmpireDataset } from "../umpires/derive.js";
import { umpireRowStatus } from "../umpires/UmpireFixtureListPanel.js";
import { capturedStamp } from "./workbook.js";
import type {
  ConsolidatedEvidenceRow,
  ConsolidatedSummaryRow,
  SlotCell,
  TechnicalFixtureRow,
  TechnicalMatrixRow,
  UmpireAiFixtureRow,
  UmpireMatrixRow,
  UmpireTmsFixtureRow,
} from "./workbook.js";

function competitionWord(gender: "m" | "w"): string {
  return gender === "w" ? "Women's" : "Men's";
}

/** `CHN v OMA`, and `TBC` where the draw has not been made — as the fixture list prints it. */
function teamsWord(home: string | null, away: string | null): string {
  return `${home ?? "TBC"} v ${away ?? "TBC"}`;
}

function cellOf(slot: { mediaName: string | null; country: string | null } | undefined): SlotCell {
  return { name: slot?.mediaName ?? null, country: slot?.country ?? null };
}

/* ── §A10 — the TO fixture list ───────────────────────────────────────────── */

/**
 * §A10's `Rule Check`: `Country conflict on {slot}`, with ` (overridden)` where §A7's two steps
 * were taken.
 *
 * **The impossible violation is carried too.** The spec's sentence names only the country rule,
 * but a slot holding the same official twice in one match has no override at all — leaving it
 * off the sheet would be the export deciding which breaches travel, which is precisely what
 * §B11's note forbids on the other module. It is written with the rule named rather than folded
 * into the country wording.
 */
export function technicalRuleCheck(slots: readonly AppointmentSlotBody[]): string {
  const notes: string[] = [];
  for (const slot of slots) {
    if (slot.mediaName === null) continue;
    const overridden = slot.status === "overridden" ? " (overridden)" : "";
    if (slot.ruleViolation === "two_roles_one_match") {
      notes.push(`Two roles in one match on ${slot.slot}${overridden}`);
      continue;
    }
    if (slot.countryConflict || slot.ruleViolation === "own_country") {
      notes.push(`Country conflict on ${slot.slot}${overridden}`);
    }
  }
  return notes.join(" · ");
}

export function technicalFixtureRows(
  fixtures: readonly AppointmentFixtureBody[],
): TechnicalFixtureRow[] {
  return fixtures.map((fixture) => {
    const slot = (name: "TO" | "SJ" | "TJ"): SlotCell =>
      cellOf(fixture.slots.find((entry) => entry.slot === name));
    return {
      competition: competitionWord(fixture.competitionGender),
      match: fixture.displayMatchNo,
      teams: teamsWord(fixture.homeTeamCode, fixture.awayTeamCode),
      date: fixture.date,
      time: fixture.time,
      pitch: fixture.pitch,
      to: slot("TO"),
      sj: slot("SJ"),
      tj: slot("TJ"),
      status: fixtureStatus(fixture).label,
      ruleCheck: technicalRuleCheck(fixture.slots),
    };
  });
}

/** §A10's matrix sheet, from the table the Appointment Matrix panel is already drawing. */
export function technicalMatrixRows(rows: readonly MatrixRow[]): TechnicalMatrixRow[] {
  return rows.map((row) => ({
    official: row.official.mediaName,
    country: row.official.country,
    role: row.official.role,
    to: row.roles[0],
    sj: row.roles[1],
    tj: row.roles[2],
    roleTotal: row.roleTotal,
  }));
}

/* ── §B11 — the two umpire fixture lists ──────────────────────────────────── */

function umpireSlots(fixture: UmpireFixtureBody, dataset: UmpireDataset): UmpireSlotBody[] {
  return dataset === "tmsActual" ? fixture.tmsActual : fixture.aiReference;
}

function umpireIdentity(fixture: UmpireFixtureBody, dataset: UmpireDataset) {
  const slots = umpireSlots(fixture, dataset);
  const slot = (name: "U1" | "U2" | "RU" | "VU"): SlotCell =>
    cellOf(slots.find((entry) => entry.slot === name));
  return {
    competition: competitionWord(fixture.competitionGender),
    match: fixture.displayMatchNo,
    teams: teamsWord(fixture.homeTeamCode, fixture.awayTeamCode),
    date: fixture.date,
    time: fixture.time,
    pitch: fixture.pitch,
    u1: slot("U1"),
    u2: slot("U2"),
    ru: slot("RU"),
    vu: slot("VU"),
  };
}

/**
 * §B11's `Violation Detail` — `U2: Two Roles In One Match · VU: Two Roles In One Match`.
 *
 * The caption is the server's own, on the cell, exactly as the `.vchip` prints it. Nothing here
 * decides what a violation is (Part C invariant 1); it only carries the decision into the file.
 */
export function umpireViolationDetail(slots: readonly UmpireSlotBody[]): string {
  return slots
    .filter((slot) => slot.violation !== null)
    .map((slot) => `${slot.slot}: ${slot.violation ?? ""}`)
    .join(" · ");
}

export function umpireTmsFixtureRows(
  fixtures: readonly UmpireFixtureBody[],
): UmpireTmsFixtureRow[] {
  return fixtures.map((fixture) => ({
    ...umpireIdentity(fixture, "tmsActual"),
    ruleCheck: umpireRowStatus(fixture, "tmsActual").label,
    violationDetail: umpireViolationDetail(fixture.tmsActual),
  }));
}

export function umpireAiFixtureRows(fixtures: readonly UmpireFixtureBody[]): UmpireAiFixtureRow[] {
  return fixtures.map((fixture) => ({
    ...umpireIdentity(fixture, "aiReference"),
    status: umpireRowStatus(fixture, "aiReference").label,
  }));
}

/** §B11's matrix sheet — three ledgers, and deliberately no fourth column summing them. */
export function umpireMatrixRows(
  rows: readonly DerivedUmpireMatrixRow[],
): UmpireMatrixRow[] {
  return rows.map((row) => ({
    umpire: row.umpire.mediaName,
    country: row.umpire.country,
    onField: row.onField,
    reserve: row.reserve,
    video: row.video,
  }));
}

/* ── The scope lines ──────────────────────────────────────────────────────── */

/**
 * Row 3 — which filters were applied when the button was pressed.
 *
 * `All Matches` when nothing is narrowed, because a sheet that said nothing about its scope
 * would be indistinguishable from a filtered one a week later.
 */
export function scopeLine(parts: readonly (string | null)[], count: number, noun = "match"): string {
  const named = parts.filter((part): part is string => part !== null && part !== "");
  const shown = `${String(count)} ${count === 1 ? noun : `${noun}es`}`;
  return `${named.length === 0 ? "All Matches" : named.join(" · ")} — ${shown} exported`;
}

/* ── Spec 11 §5 — the consolidated umpire-feedback export ─────────────────────
 *
 * The wire body of `GET /um/umpires/consolidated-evidence`, as workbook rows.
 *
 * **Nothing here decides what a manager may see.** The server assembled the pool — released
 * notes about each of her own umpires from every author, plus HER OWN Drafts and Locked notes
 * — and every row arrived carrying its own `visibility`. These functions carry that decision
 * into a file; they do not re-take it, and there is no predicate below that could hide a row
 * the pane would show or show one it would not.
 *
 * **Sorted by umpire, then by when the note was taken.** `mediaName` is `SURNAME Given`, as
 * TMS holds it, so ordering by it IS ordering by surname — no name is split, and none is
 * re-spelled to be sorted. Within an umpire the server's own `capturedAt, id` order is kept,
 * which is the order the pane lists them in.
 */

/** `You` for the reader's own note, whatever its state; the author's short name otherwise. */
function authorWord(note: ConsolidatedEvidenceNote): string {
  if (note.mine) return "You";
  return note.authorShortName ?? "";
}

/**
 * The `Criterion/Tag` cell.
 *
 * `Not Yet Categorised` is the pane's own name for the ungrouped bucket (spec 02 §3's
 * text-only capture, whose key *"is filled later by the AI category suggestion, never in the
 * moment"*), and it is a STATE rather than an empty slot — which is why it is written out
 * here where the module note would otherwise ask for a blank. A blank would read as a
 * spreadsheet that lost the value.
 */
function criterionWord(note: ConsolidatedEvidenceNote): string {
  return note.criterionName === null
    ? "Not Yet Categorised"
    : tidyCriterionDisplayName(note.criterionName);
}

/** Every note in the body, one row each, in umpire-then-capture order. */
export function consolidatedEvidenceRows(
  body: UmConsolidatedEvidenceBody,
  timezone: string | null,
): ConsolidatedEvidenceRow[] {
  return [...body.umpires]
    .sort((a, b) => a.mediaName.localeCompare(b.mediaName) || a.tmsPersonId.localeCompare(b.tmsPersonId))
    .flatMap((umpire) =>
      umpire.notes.map((note) => ({
        umpire: umpire.mediaName,
        country: umpire.country,
        // §H's three words, from the one place they are spelled.
        status: VISIBILITY_LABEL[note.visibility],
        author: authorWord(note),
        match: note.matchNo,
        quarter: note.quarter,
        minute: note.matchMinute,
        criterion: criterionWord(note),
        note: note.bodyEn,
        captured: capturedStamp(note.capturedAt, timezone),
      })),
    );
}

/**
 * The summary sheet — one row per umpire, in the same order as the evidence sheet.
 *
 * `authors` is how many DIFFERENT managers released a note about this umpire. It is a property
 * of the umpire's evidence base, not of any manager: nothing here counts what any one author
 * contributed, and nothing groups by author across umpires (spec 11 §6). The reader's own
 * Draft and Locked figures are hers — no other author's unreleased note is in the body at all,
 * so there is nothing else that could be counted.
 */
export function consolidatedSummaryRows(
  body: UmConsolidatedEvidenceBody,
): ConsolidatedSummaryRow[] {
  return [...body.umpires]
    .sort((a, b) => a.mediaName.localeCompare(b.mediaName) || a.tmsPersonId.localeCompare(b.tmsPersonId))
    .map((umpire) => {
      const released = umpire.notes.filter((note) => note.visibility === "released");
      return {
        umpire: umpire.mediaName,
        country: umpire.country,
        released: released.length,
        // `You` counts as one contributor like anybody else — the reader's own released notes
        // are part of the panel's shared record, and the pane attributes them the same way.
        authors: new Set(released.map((note) => (note.mine ? "You" : note.authorShortName)))
          .size,
        yourDrafts: umpire.notes.filter((note) => note.visibility === "draft").length,
        yourLocked: umpire.notes.filter((note) => note.visibility === "locked").length,
      };
    });
}

function countWord(count: number, noun: string): string {
  return `${String(count)} ${count === 1 ? noun : `${noun}s`}`;
}

/**
 * Row 3 of the evidence sheet — exactly what this file holds, said in a sentence.
 *
 * `scopeLine` above is the fixture lists' own, and its plural is `matches`; this sheet counts
 * umpires and notes, and a scope line reading `37 notees` would be the export failing at the
 * first thing it says. It is a different scope in any case: nothing here was filtered, so the
 * line names the POOL rather than the filters, which is what makes it worth reading.
 */
export function consolidatedScopeLine(umpires: number, notes: number): string {
  return (
    `${countWord(umpires, "umpire")} · Whole tournament, both competitions · ` +
    "Every released note from every Umpires Manager, plus your own Drafts and Locked " +
    `notes · Times on the tournament clock — ${countWord(notes, "note")} exported`
  );
}

/** Row 3 of the summary sheet. */
export function consolidatedSummaryScopeLine(umpires: number): string {
  return (
    `${countWord(umpires, "umpire")} · Whole tournament — released notes are counted per ` +
    "umpire, never per manager; the Draft and Locked figures are your own and nobody else's"
  );
}
